// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { sprintf } from 'sprintf-js';
import queryString from 'query-string';
import type { Location } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { trackClicked } from 'utils/analytics';
import { hasResumeDb } from 'utils/resumeDatabase';
import { pathnameEqual } from 'utils/url';
import { listingSearchHome, getHomeUrl } from 'utils/searchHomePages';
import Box from 'components/Box';
import TrackedLink from 'components/TrackedLink';
import BasePostListingLink from 'components/PostListingLink';
import Button, {
  SecondaryButtonLink,
  SecondaryButtonDangerLink
} from 'components/Button';
import PageFooter from 'components/PageFooter';
import Hide from 'components/Hide';
import PageHeaderAccountDropdown from 'components/PageHeaderAccountDropdown';
import DropdownMenu, {
  DropdownMenuItemRouter
} from 'components/PageHeaderDropdownMenu';
import LanguageSelectionMenu from 'components/LanguageSelectionMenu';
import Anchor, { RouterLink } from 'components/Link';
import { NavLink as Link } from 'components/RouterLink';
import Text, { H4, BodyMicro } from 'components/Text';
import Icon from 'components/Icon';
import Logo from 'components/Logo';
import Img from 'components/Image';
import NoAmp from 'containers/NoAmp';
import ClientOnly from 'containers/ClientOnly';
import type { CurrentUser } from 'api/currentUser';
import type { DaysMap } from 'api/idealistDaysMaps';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE } from 'utils/constants';
import { isEnabled as daysEnabled } from 'utils/idealistdayevents';
import { showModal as reduxShowModal } from '../redux/main/actions/modal';
import { setSearchFilter as setSearchFilterAction } from '../redux/main/actions/mainSearch';
import { colors, gutterWidth, transition, bp, breakpoints } from '../theme';

const { useMemo, useState, useCallback, useEffect } = React;

const NAV_MAP = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE,
  HOME: 'Idealist.org',
  GRAD_PROGRAM: getText('Grad Schools'),
  VOLOP: getText('Volunteer +'),
  BLOG: getText('Blog'),
  QUEUES: getText('Queues'),
  REPORTS: getText('Reports'),
  CREATE_PAYMENT: getText('Create Payment'),
  GRAD_FAIRS: getText('Grad Fairs'),
  EMAIL_PROMO: getText('Email Promos'),
  ACTION_LOG: getText('Action Log'),
  CMS: getText('CMS')
};

const PATH_MAP = {
  ...NAV_MAP, // for flow
  HOME: '/',
  QUEUES: 'staff/queues/assigned-to-me',
  REPORTS: 'staff/reports',
  CREATE_PAYMENT: 'staff/payments/create',
  GRAD_FAIRS: 'staff/grad-fairs',
  EMAIL_PROMO: 'staff/email-promos',
  ACTION_LOG: 'staff/action-log',
  CMS: 'cms'
};

const NAV = {
  MAIN: [
    'JOB',
    'INTERNSHIP',
    'VOLOP',
    'ORG',
    'IDEALIST_DAY_EVENT',
    'GRAD_PROGRAM',
    'GROUP',
    'BLOG'
  ],
  STAFF: [
    'HOME',
    'QUEUES',
    'REPORTS',
    'CREATE_PAYMENT',
    'ACTION_LOG',
    'GRAD_FAIRS',
    'EMAIL_PROMO',
    'CMS'
  ]
};

const getNavOptions = (section: 'MAIN' | 'STAFF') =>
  section === 'STAFF' ? NAV.STAFF : NAV.MAIN;

const MobileSectionTitle = ({
  children,
  ...props
}: {
  children?: React.Node
}) => (
  <Hide lg xl>
    <Box borderTop={`1px solid ${colors.aluminum}`} pt={40} mt={40} {...props}>
      {children && <BodyMicro color={colors.silver}>{children}</BodyMicro>}
    </Box>
  </Hide>
);

const Separator = props => (
  <Hide
    xs
    sm
    md
    width="1px"
    height={20}
    bg={colors.silver}
    opacity="0.5"
    {...props}
  />
);

const NavLink = styled(
  ({ active, navInactive, fullSize, isStaff, type, to, ...props }) =>
    (isStaff &&
      ['CMS', 'HOME', 'USER_DASHBOARD', 'ORG_DASHBOARD'].includes(type)) ||
    (type === 'BLOG' && USER_LOCALE === 'es') ? (
      <Box
        component="a"
        classes={['flex', 'align-center']}
        href={to}
        {...props}
      />
    ) : (
      <Box
        component={Link}
        classes={['flex', 'align-center']}
        to={to}
        {...props}
        onClick={() => {
          trackClicked(`${NAV_MAP[type]} Link Header`);
          if (props.onClick) {
            props.onClick();
          }
        }}
      />
    )
)`
  position: relative;
  padding: ${(2.5 - 1.375) / 2}em 0;
  height: 100%;
  overflow: hidden;
  color: ${colors.greyishBrown};
  opacity: ${({ navInactive }) => (navInactive ? 1 : 0.7)};
  font-size: inherit;
  line-height: 1.375;
  text-align: left;
  transition: opacity ${transition};
  &::after {
    content: '';
    position: absolute;
    transform: translateY(100%);
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${props => (props.fullSize ? 4 : 2)}px;
    background: ${colors.waterBlue};
  }
  ${props =>
    props.active &&
    `
    opacity: 1;
    &::after {
      transform: translateY(0);
    }
  `}
  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }
  &:hover {
    opacity: 1;
    color: ${colors.waterBlue};
  }
  @media (min-width: ${breakpoints[2]}) {
    width: 100%;
    padding: 0;
    text-align: center;
    line-height: inherit;
  }
`;

const LoginLink = styled.a`
  color: ${colors.battleshipGrey};
  font-size: inherit;
  transition: color ${transition};
  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${colors.waterBlue};
  }
`;

const StyledLogo = styled(Logo)`
  height: 36px;
  @media (min-width: ${breakpoints[2]}) {
    height: 28px;
  }
  @media (min-width: 1350px) {
    height: 36px;
  }
`;

const PageHeaderContainer = styled(Box)`
  ${({ $fullSize }) => `
    font-size: 20px;
    line-height: 2.5;
    @media (min-width: ${breakpoints[2]}) {
      font-size: ${USER_LOCALE === 'en' || !$fullSize ? 13 : 12}px;
      line-height: normal;
    }
    ${
      USER_LOCALE !== 'en' && $fullSize
        ? `
      @media (min-width: 1350px) {
        font-size: 13px;
      }
    `
        : ''
    }
    @media (min-width: ${USER_LOCALE === 'en' || !$fullSize ? 1400 : 1475}px) {
      font-size: 14px;
    }
    ${
      $fullSize
        ? `
      @media (min-width: ${USER_LOCALE === 'en' ? 1450 : 1600}px) {
        font-size: 15px;
      }
    `
        : ''
    }
  `}
  pointer-events: none;
  @media (min-width: ${breakpoints[2]}) {
    pointer-events: auto;
  }
`;

const NavLinks = styled(Box)`
  @media (max-width: ${bp[2] - 1}px) {
    position: fixed;
    bottom: 100%;
    left: 0;
    z-index: 100;
    width: 100%;
    height: 100%;
    max-width: 100%;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    background: white;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
    transition: transform ${transition};
    will-change: transform;
    ${props =>
      props.$menuOpen
        ? `
      transform: translateY(100%);
    `
        : `
      transform: translateY(-6px);
    `}
  }
`;

const PostListingLink = () => (
  <BasePostListingLink
    onClick={() => {
      trackClicked('Post A Listing', {
        context: 'Header'
      });
    }}
  >
    <Box classes={['flex', 'align-center']}>
      <Box mr={[16, null, null, '6px']} mt="1px" opacity="0.8">
        <Icon
          name="pin"
          width={[16, null, null, 12]}
          height={[16, null, null, 12]}
          fill="currentColor"
        />
      </Box>
      <Text fontSize="inherit" whiteSpace="nowrap">
        {getText('Post a Listing')}
      </Text>
    </Box>
  </BasePostListingLink>
);

const AmpPostListingLink = () => (
  <Anchor href="/post-a-listing">
    <Box classes={['flex', 'align-center']}>
      <Box mr={[16, null, null, '6px']} mt="1px" opacity="0.8">
        <Icon
          name="pin"
          width={[16, null, null, 12]}
          height={[16, null, null, 12]}
          fill="currentColor"
        />
      </Box>
      <Text fontSize="inherit">{getText('Post a Listing')}</Text>
    </Box>
  </Anchor>
);

const PostGroupLink = () => (
  <RouterLink
    to="/groups/create"
    data-qa-id="create-a-group"
    onClick={() => {
      trackClicked('Create a Group', {
        context: 'Header'
      });
    }}
  >
    <Box classes={['flex', 'align-center']}>
      <Box mr={[16, null, null, '6px']} mt="1px" opacity="0.8">
        <Icon
          name="pin"
          width={[16, null, null, 12]}
          height={[16, null, null, 12]}
          fill="currentColor"
        />
      </Box>
      <Text fontSize="inherit">{getText('Create a Group')}</Text>
    </Box>
  </RouterLink>
);

const ForOrganizations = () => (
  <Hide xs sm md lg>
    <NoAmp>
      <Box classes={['flex', 'align-center']}>
        <Separator />

        <Box mt="1px">
          <DropdownMenu
            title={() => getText('For Organizations')}
            right
            data-qa-id="for-org-dropdown"
            onClick={() => {
              trackClicked('For Organizations');
            }}
            hideArrow={{ lg: true }}
          >
            {() => (
              <>
                <BasePostListingLink
                  component={DropdownMenuItemRouter}
                  data-qa-id="post-a-listing-dropdown"
                  onClick={() => {
                    trackClicked('Post A Listing', {
                      context: 'Header (For Organizations Dropdown)'
                    });
                  }}
                >
                  {getText('Post a Listing')}
                </BasePostListingLink>
                <DropdownMenuItemRouter
                  to="/organizations/create"
                  data-qa-id="create-org"
                >
                  {getText('Add Your Organization')}
                </DropdownMenuItemRouter>
              </>
            )}
          </DropdownMenu>
        </Box>
      </Box>
    </NoAmp>
  </Hide>
);

const TopNavLink = ({
  navSection,
  index,
  linkTo,
  isStaff,
  type,
  setSearchFilter,
  isActive,
  navInactive,
  fullSize,
  external
}: {
  navSection: string,
  index: number,
  linkTo: string,
  isStaff: boolean,
  type: string,
  setSearchFilter: (string, string) => mixed,
  isActive: string => boolean,
  navInactive: boolean,
  fullSize?: boolean,
  external?: boolean
}) => {
  const additionalProps = external
    ? {
        target: '_blank'
      }
    : {};
  return (
    <Box
      position="relative"
      height={[null, null, null, '100%']}
      display={['inline-block', null, null, 'flex']}
      alignItems="center"
    >
      <NavLink
        data-qa-id={`nav-${type.toLowerCase().replace('_', '-')}-link`}
        to={linkTo}
        onClick={() => {
          // Staff use old method of changing types (in StaffSearchInputsContainer)
          // Regular users use CMS pages / updated SearchInputsContainer
          if (!isStaff) {
            setSearchFilter('type', type);
          }
        }}
        isStaff={isStaff}
        active={isActive(type)}
        navInactive={navInactive}
        fullSize={fullSize}
        type={type}
        mr={index < NAV[navSection].length - 1 && '1.5em'}
        {...additionalProps}
      >
        <Box classes={['flex', 'align-center']}>
          {NAV_MAP[type]}
          {external && (
            <Box transform="translateZ(0)" ml="4px">
              <Icon
                name="external-link"
                width={9}
                height={9}
                stroke="currentColor"
                strokeWidth="6px"
                display="block"
              />
            </Box>
          )}
        </Box>
      </NavLink>
    </Box>
  );
};

const PageHeader = ({
  url,
  navSection = 'MAIN',
  location,
  toggleSearch,
  withSearchHero,
  requestIsAmp,
  showModal,
  currentUser,
  daysMap,
  setSearchFilter
}: {
  url: Object,
  navSection?: 'MAIN' | 'STAFF',
  location: Location,
  toggleSearch: Function,
  withSearchHero?: boolean,
  requestIsAmp?: boolean,
  showModal: Function,
  currentUser: CurrentUser,
  daysMap: DaysMap,
  setSearchFilter: (string, string) => mixed
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const redirectTo = location ? `${location.pathname}${location.search}` : '';
  const redirectParam = queryString.stringify({ to: redirectTo });
  const params = location ? queryString.parse(location.search) : {};
  const isStaff = navSection === 'STAFF';
  const mapState = daysMap.mapState;
  const fullSize = withSearchHero || requestIsAmp;
  const isActive = useCallback(
    type => {
      // $FlowFixMe: Flow can't handle the staff-specific types
      const h = listingSearchHome(type);
      // staff
      return isStaff
        ? location &&
            type !== 'HOME' &&
            (location.pathname.includes(PATH_MAP[type]) ||
              (type === 'QUEUES' && location.pathname.includes('staff/queues')))
        : // non-staff: grad program special-cased
          (h && pathnameEqual(h, location.pathname)) || params.type === type;
    },
    [isStaff, location, params.type]
  );
  const navInactive = (isStaff ? NAV.STAFF : NAV.MAIN).every(t => !isActive(t));
  const nonAmpUrl = location.pathname.replace('/amp/', '/').replace(/#.+$/, '');

  const clickLogin = useCallback(
    e => {
      e.preventDefault();
      trackClicked('Log In Header');
      showModal('LOGIN', {
        redirectTo,
        manualLogin: true
      });
    },
    [redirectTo, showModal]
  );

  const closeSearch = () => setMenuOpen(false);

  useEffect(
    () => {
      if (location.hash === '#search') {
        toggleSearch(true);
      } else if (location.hash === '#menu') {
        setMenuOpen(true);
      }
    },
    [location.hash, toggleSearch]
  );

  const mainLinks = useMemo(
    () => (
      <>
        {getNavOptions(navSection).map((type, index) => {
          const isSpanishBlog = type === 'BLOG' && USER_LOCALE === 'es';
          const linkTo =
            type === 'HOME'
              ? '/'
              : isStaff
              ? `/${PATH_MAP[type] ? PATH_MAP[type] : '/staff'}`
              : isSpanishBlog
              ? 'https://blog.es.idealist.org/'
              : getHomeUrl(((type: any): ListingType));
          return (
            <Box height="100%" key={type}>
              {type === 'IDEALIST_DAY_EVENT' ? (
                <ClientOnly>
                  {daysEnabled() && (
                    <TopNavLink
                      navSection={navSection}
                      index={index}
                      linkTo={linkTo}
                      isStaff={isStaff}
                      type={type}
                      setSearchFilter={setSearchFilter}
                      isActive={isActive}
                      navInactive={navInactive}
                      fullSize={fullSize}
                    />
                  )}
                </ClientOnly>
              ) : (
                <TopNavLink
                  navSection={navSection}
                  index={index}
                  linkTo={linkTo}
                  isStaff={isStaff}
                  type={type}
                  setSearchFilter={setSearchFilter}
                  isActive={isActive}
                  navInactive={navInactive}
                  fullSize={fullSize}
                  external={isSpanishBlog}
                />
              )}
            </Box>
          );
        })}
      </>
    ),
    [navSection, isStaff, setSearchFilter, isActive, navInactive, fullSize]
  );

  const mobileLogin = useMemo(
    () => (
      <ClientOnly>
        {currentUser.id ? (
          <SecondaryButtonDangerLink
            href="/logout"
            size="large"
            width="100%"
            px="0 !important"
            py="17px !important"
          >
            {getText('Log Out')}
          </SecondaryButtonDangerLink>
        ) : (
          <SecondaryButtonLink
            size="large"
            width="100%"
            px="0 !important"
            py="17px !important"
            blue
            href={`/login?${redirectParam}`}
            onClick={clickLogin}
            data-qa-id="login-button-link"
            aria-label={getText('Log In')}
          >
            <Hide lg>{getText('Log In')}</Hide>
            <Hide xs sm md xl>
              <Icon
                name="person"
                display="block"
                width={16}
                height={16}
                fill="currentColor"
                opacity="0.8"
              />
            </Hide>
          </SecondaryButtonLink>
        )}
      </ClientOnly>
    ),
    [clickLogin, currentUser.id, redirectParam]
  );

  return (
    <PageHeaderContainer
      $fullSize={fullSize}
      position="relative"
      zIndex="101"
      height={[60, null, null, fullSize ? 72 : 40]}
    >
      <Box
        classes={['flex', 'align-center', 'justify-between']}
        position={['fixed', null, null, 'relative']}
        top={0}
        left={0}
        width={1}
        height={[60, null, null, fullSize ? 72 : 40]}
        px={gutterWidth / 2}
        bg="white"
        boxShadow={['0 2px 4px rgba(0,0,0,0.075)', null, null, 'none']}
        pointerEvents="auto"
      >
        {requestIsAmp ? (
          <Box mr={24}>
            <Box
              component={TrackedLink}
              href="/"
              title="Idealist"
              eventName="Idealist Logo Header"
              data-qa-id="header-logo-link"
              classes={['flex']}
            >
              <Img
                src="/static/images/logo.png"
                srcSet="/static/images/logo.png, /static/images/logo@2x.png 2x, /static/images/logo@3x.png 3x"
                width={114}
                height={36}
                alt="Idealist.org"
              />
            </Box>
          </Box>
        ) : (
          <Box
            mr={24}
            flex="0 0 auto"
            display={[null, null, null, !fullSize && 'none']}
          >
            <TrackedLink
              href={isStaff ? '/staff' : '/'}
              title={`Idealist${isStaff ? ' Staff' : ''}`}
              eventName="Idealist Logo Header"
              data-qa-id="header-logo-link"
            >
              <StyledLogo type={isStaff ? 'STAFF' : undefined} />
            </TrackedLink>
          </Box>
        )}

        <Hide lg xl>
          <Box ml="auto" classes={['flex', 'align-center']}>
            {requestIsAmp ? (
              <>
                <a
                  href={`${nonAmpUrl}#search`}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon
                    name="search"
                    display="block"
                    width={16}
                    height={16}
                    fill={colors.charcoalGrey}
                  />
                </a>

                <Box
                  as="a"
                  href={`${nonAmpUrl}#menu`}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                >
                  <Icon
                    name="hamburger"
                    display="block"
                    width={22}
                    height={22}
                    fill={colors.charcoalGrey}
                  />
                </Box>
              </>
            ) : (
              <>
                <Button
                  onClick={toggleSearch}
                  aria-label={getText('Toggle search')}
                  title={getText('Toggle search')}
                  data-qa-id="search-menu-button"
                >
                  <Icon
                    name="search"
                    display="block"
                    width={16}
                    height={16}
                    fill="currentColor"
                  />
                </Button>

                <Button
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label={getText('Toggle menu')}
                  title={getText('Toggle menu')}
                  ml={16}
                  data-qa-id="mobile-menu-button"
                >
                  <Icon
                    name="hamburger"
                    display="block"
                    width={22}
                    height={22}
                    fill="currentColor"
                  />
                </Button>
              </>
            )}
          </Box>
        </Hide>

        <NavLinks
          flex="1 1 auto"
          $menuOpen={menuOpen}
          onClick={() => setMenuOpen(false)}
          height={[null, null, null, '100%']}
        >
          <Hide lg xl height={[null, null, null, '100%']}>
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.aluminum}`}
            >
              <H4 mb={0} color={colors.waterBlue}>
                {currentUser.id
                  ? /* TRANSLATORS: user greeting at top of page */ sprintf(
                      getText('Hi, %(name)s!'),
                      {
                        name: currentUser.firstName || currentUser.email
                      }
                    )
                  : getText('Menu')}
              </H4>

              <Button
                onClick={closeSearch}
                aria-label={getText('Close search')}
                ml="auto"
              >
                <Icon
                  name="close"
                  display="block"
                  width={16}
                  height={16}
                  stroke={colors.waterBlue}
                />
              </Button>
            </Box>
          </Hide>

          <Box
            display={['block', null, null, 'flex']}
            height={[null, null, null, '100%']}
            justifyContent="space-between"
            p={[gutterWidth / 2, null, null, 0]}
            pb={[0, null, null, 0]}
          >
            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              mt="1px"
            >
              <MobileSectionTitle borderTop="none" pt={0} mt={0}>
                {getText('Find something to do')}
              </MobileSectionTitle>

              {mainLinks}
            </Box>

            <Box
              display={['block', null, null, 'flex']}
              alignItems="center"
              ml="auto"
              mt={[0, null, null, '1px']}
            >
              {currentUser.id ? (
                <>
                  <Hide lg xl>
                    <MobileSectionTitle>
                      {getText('Settings')}
                    </MobileSectionTitle>

                    {hasResumeDb(currentUser) && (
                      <NavLink
                        isStaff={isStaff}
                        type="USER_DASHBOARD"
                        to="/dashboard/employment-profile"
                      >
                        {getText('Employment Profile')}
                      </NavLink>
                    )}

                    <NavLink
                      isStaff={isStaff}
                      type="USER_DASHBOARD"
                      to="/dashboard/account-settings"
                    >
                      {getText('My Account')}
                    </NavLink>

                    {currentUser.orgs && currentUser.orgs.length > 0 && (
                      <>
                        <MobileSectionTitle>
                          {getText('My Organizations')}
                        </MobileSectionTitle>

                        {currentUser.orgs.map(org => (
                          <Box key={org.id}>
                            <NavLink
                              isStaff={isStaff}
                              type="ORG_DASHBOARD"
                              to={
                                org.hasGradProgram
                                  ? `/admin/${org.id}/grad-program`
                                  : `/admin/${org.id}`
                              }
                            >
                              {org.name}
                            </NavLink>
                          </Box>
                        ))}
                      </>
                    )}
                  </Hide>

                  <ClientOnly>
                    <Hide xs sm md>
                      <Box mt="1px">
                        <PageHeaderAccountDropdown
                          currentUser={currentUser}
                          isStaff={isStaff}
                        />
                      </Box>
                    </Hide>
                  </ClientOnly>

                  <Separator />
                </>
              ) : (
                <Box display={['none', null, null, 'flex']} alignItems="center">
                  <LoginLink
                    href={`/login?${redirectParam}`}
                    onClick={clickLogin}
                    data-qa-id="login-link"
                    aria-label={getText('Log In')}
                  >
                    {(currentUser.isLoaded || requestIsAmp) &&
                      getText('Log In')}
                  </LoginLink>
                  <Separator ml={12} />
                </Box>
              )}

              <Hide xs sm md>
                {requestIsAmp ? (
                  <Box
                    component={LoginLink}
                    mx={12}
                    classes={['flex', 'align-center']}
                    href={`${nonAmpUrl}#search`}
                    aria-label={getText('Toggle search')}
                    title={getText('Toggle search')}
                    data-qa-id="search-menu-button"
                  >
                    <Box mr="6px">
                      <Icon
                        name="search"
                        display="block"
                        width={12}
                        height={12}
                        fill="currentColor"
                      />
                    </Box>
                    {getText('Search')}
                  </Box>
                ) : (
                  <Box mt="1px">
                    <LanguageSelectionMenu url={url} />
                  </Box>
                )}
              </Hide>

              {!isStaff && (
                <>
                  <ForOrganizations location={location} />

                  <Separator mr={12} />

                  <MobileSectionTitle />

                  {mapState === 'GROUPS' ? (
                    <PostGroupLink />
                  ) : !requestIsAmp ? (
                    <PostListingLink location={location} />
                  ) : (
                    <AmpPostListingLink />
                  )}

                  <Hide lg xl>
                    <NavLink to="/organizations/create" data-qa-id="create-org">
                      <Box classes={['flex', 'align-center']}>
                        <Box
                          mr={[14, null, null, '6px']}
                          mt="4px"
                          opacity="0.8"
                        >
                          <Icon
                            name="circle-add"
                            width={18}
                            height={18}
                            fill="currentColor"
                          />
                        </Box>
                        <Text fontSize="inherit" fontWeight="normal">
                          {getText('Add Your Organization')}
                        </Text>
                      </Box>
                    </NavLink>

                    <MobileSectionTitle />

                    {mobileLogin}

                    <Box
                      width={`calc(100% + ${gutterWidth}px)`}
                      ml={-gutterWidth / 2}
                      mt={gutterWidth}
                      borderBottom={`6px solid ${colors.waterBlue}`}
                    >
                      <PageFooter url={url} inPageHeader />
                    </Box>
                  </Hide>
                </>
              )}
            </Box>
          </Box>
        </NavLinks>
      </Box>
    </PageHeaderContainer>
  );
};

const mapStateToProps = ({ currentUser, daysMap }) => ({
  currentUser,
  daysMap
});

export default withRouter(
  connect(
    mapStateToProps,
    { showModal: reduxShowModal, setSearchFilter: setSearchFilterAction }
  )(PageHeader)
);
